import React, { useState, useEffect } from 'react';
import { schoolListRequestsAPI } from '../../utils/api';

const STATUS_COLORS = {
  new:        { bg: '#dbeafe', color: '#1d4ed8', label: 'New' },
  'in-review':{ bg: '#fef3c7', color: '#92400e', label: 'In Review' },
  completed:  { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
  closed:     { bg: '#f3f4f6', color: '#6b7280', label: 'Closed' },
};

const Badge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span style={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: '0.72rem', padding: '0.25rem 0.65rem', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {s.label}
    </span>
  );
};

const DetailModal = ({ req, onClose, onUpdate }) => {
  const [status, setStatus] = useState(req.status);
  const [notes, setNotes] = useState(req.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await schoolListRequestsAPI.updateStatus(req.id, { status, admin_notes: notes });
      onUpdate(res.data);
      onClose();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '16px 16px 0 0', padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>#{req.id} · {req.school_name}</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', marginTop: 2 }}>
              {new Date(req.created_at).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: '1rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {[
              ['School', `${req.school_name}${req.school_city ? `, ${req.school_city}` : ''}${req.school_state ? ` ${req.school_state}` : ''}`],
              ['Grade', req.grade || '—'],
              ['Parent', req.parent_name],
              ['Email', req.email],
              ['Phone', req.phone || '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#f8fafc', borderRadius: 8, padding: '0.6rem 0.85rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{k}</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1f2937', wordBreak: 'break-all' }}>{v}</div>
              </div>
            ))}
          </div>

          {/* List text */}
          {req.list_text && (
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Supply List</div>
              <pre style={{ background: '#f8fafc', borderRadius: 8, padding: '0.85rem', fontSize: '0.83rem', color: '#374151', whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{req.list_text}</pre>
            </div>
          )}

          {/* File attachment */}
          {req.file_name && req.file_data && (
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Attached File</div>
              <a href={req.file_data} download={req.file_name}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#eef2ff', color: '#4f46e5', fontWeight: 600, fontSize: '0.85rem', padding: '0.5rem 0.9rem', borderRadius: 8, textDecoration: 'none' }}>
                📎 {req.file_name}
              </a>
            </div>
          )}

          {/* Status + notes */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Update Status</label>
            <select className="form-input" value={status} onChange={e => setStatus(e.target.value)} style={{ marginBottom: '0.75rem' }}>
              {Object.entries(STATUS_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Admin Notes</label>
            <textarea className="form-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal notes about this request…" style={{ resize: 'vertical', fontFamily: 'inherit' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <button onClick={onClose} className="btn btn-outline">Cancel</button>
            <button onClick={save} disabled={saving} className="btn btn-primary" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminSchoolRequests = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);

  const load = (status = filterStatus) => {
    setLoading(true);
    schoolListRequestsAPI.getAll({ status: status || undefined })
      .then(r => { setRequests(r.data.requests); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    schoolListRequestsAPI.getAll({})
      .then(r => { setRequests(r.data.requests); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleFilter = (s) => { setFilterStatus(s); load(s); };

  const handleUpdate = (updated) => {
    setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete request from "${name}"?`)) return;
    await schoolListRequestsAPI.remove(id);
    setRequests(prev => prev.filter(r => r.id !== id));
    setTotal(t => t - 1);
  };

  const counts = requests.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>School List Requests</h1>
          <p style={{ color: '#6b7280', fontSize: '0.88rem', marginTop: 2 }}>Custom kit requests from parents who couldn't find their school</p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 800, fontSize: '1.1rem', borderRadius: 10, padding: '0.5rem 1rem' }}>
          {total} total
        </div>
      </div>

      {/* Status summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {Object.entries(STATUS_COLORS).map(([k, v]) => (
          <div key={k} onClick={() => handleFilter(filterStatus === k ? '' : k)}
            style={{ background: filterStatus === k ? v.bg : '#fff', border: `2px solid ${filterStatus === k ? v.color : '#e5e7eb'}`, borderRadius: 12, padding: '0.85rem 1rem', cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: v.color }}>{counts[k] || 0}</div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginTop: 2 }}>{v.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📭</div>
            <div style={{ fontWeight: 600 }}>No requests yet</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th}>#</th>
                  <th style={th}>School</th>
                  <th style={th}>Parent</th>
                  <th style={th}>Grade</th>
                  <th style={th}>Date</th>
                  <th style={th}>Status</th>
                  <th style={th}>List?</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={td}><span style={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 700 }}>#{r.id}</span></td>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>{r.school_name}</div>
                      {r.school_city && <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{r.school_city}{r.school_state ? `, ${r.school_state}` : ''}</div>}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.parent_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{r.email}</div>
                    </td>
                    <td style={{ ...td, color: '#6b7280' }}>{r.grade || '—'}</td>
                    <td style={{ ...td, color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td style={td}><Badge status={r.status} /></td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {(r.list_text || r.file_name) ? <span style={{ color: '#10b981', fontSize: '1rem' }}>✓</span> : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => setSelected(r)} className="btn btn-sm" style={{ background: '#eef2ff', color: '#4f46e5', border: 'none' }}>View</button>
                        <button onClick={() => handleDelete(r.id, r.parent_name)} className="btn btn-sm" style={{ background: '#fee2e2', color: '#dc2626', border: 'none' }}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <DetailModal req={selected} onClose={() => setSelected(null)} onUpdate={handleUpdate} />}
    </div>
  );
};

const th = { padding: '0.7rem 0.85rem', fontWeight: 700, color: '#374151', textAlign: 'left' };
const td = { padding: '0.7rem 0.85rem', verticalAlign: 'top' };

export default AdminSchoolRequests;
